import React, { useState } from "react";
import { PageHero, Section, Eyebrow, SectionTitle } from "../components/Primitives";
import { Mail, Phone, MapPin, Send, CheckCircle2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const Contact = () => {
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });

  const onSubmit = (e) => {
    e.preventDefault();
    // Frontend-only: build a mailto so a real message is delivered without a backend.
    const body = `Name: ${form.name}%0D%0AEmail: ${form.email}%0D%0A%0D%0A${encodeURIComponent(form.message)}`;
    window.location.href = `mailto:info@vanhanhcanberra.com?subject=${encodeURIComponent(form.subject || "Enquiry via website")}&body=${body}`;
    setSent(true);
  };

  return (
    <>
      <PageHero
        eyebrow="Get in touch"
        title="Write to us. Ring us. Or simply drop by."
        subtitle="There is a person on the other end of every message — usually a volunteer, sometimes a monk, always someone glad you reached out."
      />

      <Section>
        <div className="grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5 space-y-8">
            <div>
              <Eyebrow>Direct</Eyebrow>
              <SectionTitle className="mt-4 text-3xl md:text-4xl">The quickest ways to reach us.</SectionTitle>
            </div>

            {[
              { Icon: Mail, t: "Email", v: "info@vanhanhcanberra.com", href: "mailto:info@vanhanhcanberra.com" },
              { Icon: Phone, t: "Phone", v: "(02) 6257 5280", href: "tel:+61262575280" },
              { Icon: MapPin, t: "Visit", v: "32 Archibald Street, Lyneham ACT 2602", href: "https://www.google.com/maps/search/?api=1&query=32+Archibald+Street+Lyneham+ACT+2602" },
            ].map(({ Icon, t, v, href }) => (
              <a key={t} href={href} target={t === "Visit" ? "_blank" : undefined} rel="noopener noreferrer" className="flex gap-4 items-start p-6 border border-[#e9dcc0] bg-[#fdfaf3] hover:border-[#c9a15b] transition-colors duration-300">
                <Icon size={22} className="text-[#7a2e1f] mt-1" />
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-[#8a6b3f]">{t}</div>
                  <div className="font-serif text-xl text-[#3d2817] mt-1 break-words">{v}</div>
                </div>
              </a>
            ))}

            <div className="p-6 bg-[#f6efe0] border border-[#e9dcc0]">
              <div className="text-xs uppercase tracking-[0.25em] text-[#8a6b3f]">Office hours</div>
              <div className="font-serif text-lg text-[#3d2817] mt-2">Mon–Fri · 10 am – 4 pm</div>
              <p className="text-sm text-[#5a4a3a] mt-2">Outside office hours, please leave a message — someone will call you back within a day.</p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="border border-[#e9dcc0] p-8 md:p-10 bg-[#fdfaf3]">
              <Eyebrow>Send a note</Eyebrow>
              <h2 className="font-serif text-3xl md:text-4xl text-[#3d2817] mt-4">Tell us what’s on your mind.</h2>
              <p className="mt-3 text-[#5a4a3a]">Whether it’s a question, an offer to help, a prayer request or a concern — we read every message.</p>

              {sent ? (
                <div className="mt-8 p-6 border border-[#c9a15b] bg-[#f6efe0] flex items-start gap-3">
                  <CheckCircle2 className="text-[#7a2e1f] mt-0.5" size={22} />
                  <div>
                    <div className="font-serif text-xl text-[#3d2817]">Thank you.</div>
                    <p className="text-sm text-[#5a4a3a] mt-1">Your email app should now be open. If it didn’t, please write to <a href="mailto:info@vanhanhcanberra.com" className="underline">info@vanhanhcanberra.com</a>.</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="mt-8 space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="name">Your name</Label>
                      <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 bg-white" placeholder="Anh Nguyen" />
                    </div>
                    <div>
                      <Label htmlFor="email">Your email</Label>
                      <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 bg-white" placeholder="you@example.com" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="subject">Subject</Label>
                    <Input id="subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-2 bg-white" placeholder="How I’d like to help / a question / a prayer request" />
                  </div>
                  <div>
                    <Label htmlFor="message">Message</Label>
                    <Textarea id="message" required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2 bg-white" placeholder="Share as much or as little as you like." />
                  </div>
                  <button type="submit" className="btn-primary">Send message <Send size={16} /></button>
                  <p className="text-xs text-[#8a6b3f]">We’ll never share your details. Messages are answered by a small team of volunteers.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </Section>
    </>
  );
};

export default Contact;
